import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Search, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/lib/api';

/**
 * Who changed what, and what it was before.
 *
 * Reads the trail eleven modules have been writing all along; nothing here
 * records anything. A row opens to show the fields that actually moved rather
 * than the whole record, because "what changed" is the question being asked.
 *
 * Where no person is named the row says "system" — imports and workers write
 * without a signed-in user. That is a real gap in the trail, so it is shown as
 * one instead of being left blank and read as a rendering fault.
 */

interface Row {
  id: string; table_name: string; record_id: string; action: string;
  changed_at: string; ip_address: string | null; reason: string | null;
  before_data: Record<string, unknown> | null;
  after_data: Record<string, unknown> | null;
  diff: Record<string, unknown> | null;
  changed_by_name: string | null; changed_by_email: string | null;
}

const TONE: Record<string, string> = {
  insert: 'border-emerald-500/50 text-emerald-600 dark:text-emerald-400',
  update: 'border-sky-500/50 text-sky-600 dark:text-sky-400',
  delete: 'border-rose-500/50 text-rose-600 dark:text-rose-400',
};

const when = (s: string) =>
  new Date(s).toLocaleString('en-GB', { timeZone: 'Asia/Riyadh', hour12: false }).slice(0, 17);

/** The fields that moved, from whichever of the three shapes is populated. */
function changes(r: Row): Array<[string, unknown, unknown]> {
  if (r.diff && typeof r.diff === 'object') {
    return Object.entries(r.diff).map(([k, v]) => {
      const o = v as Record<string, unknown>;
      if (o && typeof o === 'object' && ('from' in o || 'to' in o)) return [k, o.from, o.to];
      return [k, r.before_data?.[k], v];
    });
  }
  const before = r.before_data ?? {};
  const after = r.after_data ?? {};
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
  return keys
    .filter((k) => JSON.stringify(before[k]) !== JSON.stringify(after[k]))
    .map((k) => [k, before[k], after[k]]);
}

const show = (v: unknown) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v).slice(0, 80);
  return String(v).slice(0, 80);
};

export default function AuditLogPage() {
  const [table, setTable] = useState('');
  const [action, setAction] = useState('');
  const [userId, setUserId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const LIMIT = 100;

  const { data: facets } = useQuery({
    queryKey: ['audit-facets'],
    queryFn: async () => (await apiClient.get<{
      tables: Array<{ table_name: string; n: number }>;
      users: Array<{ id: string; name: string; n: number }>;
    }>('/api/v1/audit/facets')).data,
  });

  const params = {
    ...(table && { table }), ...(action && { action }), ...(userId && { userId }),
    ...(from && { from }), ...(to && { to }), ...(search.trim() && { search: search.trim() }),
    limit: LIMIT, offset,
  };

  const { data, isLoading } = useQuery({
    queryKey: ['audit', params],
    queryFn: async () => (await apiClient.get<{ items: Row[]; total: number }>(
      '/api/v1/audit', { params })).data,
  });

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;

  const reset = <T,>(set: (v: T) => void) => (v: T) => { set(v); setOffset(0); };

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-[1500px] mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-display text-lg">Change log</div>
            <div className="text-xs text-muted-foreground">Who changed what, and what it was before.</div>
          </div>
          <Link to="/dashboard/settings">
            <Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Settings</Button>
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-[1500px] mx-auto w-full px-6 py-5 space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="relative flex-1 min-w-[12rem] max-w-xs">
            <Search className="h-4 w-4 absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => reset(setSearch)(e.target.value)}
              className="ps-9" placeholder="Person, record id or reason…" />
          </div>

          <select value={table} onChange={(e) => reset(setTable)(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-2 text-sm">
            <option value="">All tables</option>
            {(facets?.tables ?? []).map((t) => (
              <option key={t.table_name} value={t.table_name}>{t.table_name} ({t.n})</option>
            ))}
          </select>

          <select value={action} onChange={(e) => reset(setAction)(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-2 text-sm">
            <option value="">All actions</option>
            {['insert', 'update', 'delete'].map((a) => <option key={a} value={a}>{a}</option>)}
          </select>

          <select value={userId} onChange={(e) => reset(setUserId)(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-2 text-sm max-w-[14rem]">
            <option value="">Anyone</option>
            {(facets?.users ?? []).map((u) => <option key={u.id} value={u.id}>{u.name} ({u.n})</option>)}
          </select>

          <input type="date" value={from} onChange={(e) => reset(setFrom)(e.target.value)}
            aria-label="From" className="h-10 rounded-md border border-input bg-background px-2 text-sm" />
          <input type="date" value={to} onChange={(e) => reset(setTo)(e.target.value)}
            aria-label="To" className="h-10 rounded-md border border-input bg-background px-2 text-sm" />

          <span className="text-sm text-muted-foreground ms-auto">
            {isLoading ? 'loading…' : `${total.toLocaleString('en-US')} entries`}
          </span>
        </div>

        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground text-xs">
              <tr>
                <th className="text-start px-3 py-2 font-medium">When</th>
                <th className="text-start px-3 py-2 font-medium">Who</th>
                <th className="text-start px-3 py-2 font-medium">Action</th>
                <th className="text-start px-3 py-2 font-medium">Table</th>
                <th className="text-start px-3 py-2 font-medium">Record</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin inline" /> Loading…
                </td></tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                  Nothing matches these filters.
                </td></tr>
              )}
              {rows.map((r) => {
                const isOpen = open === r.id;
                const list = isOpen ? changes(r) : [];
                return (
                  <>
                    <tr key={r.id} onClick={() => setOpen(isOpen ? null : r.id)}
                      className="border-t border-border cursor-pointer hover:bg-muted/20 transition-colors">
                      <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{when(r.changed_at)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {r.changed_by_name ?? <span className="text-muted-foreground italic">system</span>}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-[11px] rounded-full border px-2 py-0.5 ${
                          TONE[r.action] ?? 'border-border text-muted-foreground'}`}>{r.action}</span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{r.table_name}</td>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                        {String(r.record_id ?? '').slice(0, 8)}
                      </td>
                      <td className="px-3 py-2 text-end text-muted-foreground">
                        {isOpen ? <ChevronDown className="h-4 w-4 inline" /> : <ChevronRight className="h-4 w-4 inline" />}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr key={`${r.id}-d`} className="bg-muted/10">
                        <td colSpan={6} className="px-4 py-3">
                          {r.reason && (
                            <div className="text-xs text-muted-foreground mb-2">Reason: {r.reason}</div>
                          )}
                          {list.length === 0 ? (
                            <div className="text-xs text-muted-foreground">
                              No before/after was recorded for this entry.
                            </div>
                          ) : (
                            <div className="rounded-lg border border-border overflow-hidden">
                              <table className="w-full text-xs">
                                <thead className="bg-muted/40 text-muted-foreground">
                                  <tr>
                                    <th className="text-start px-3 py-1.5 font-medium">Field</th>
                                    <th className="text-start px-3 py-1.5 font-medium">Was</th>
                                    <th className="text-start px-3 py-1.5 font-medium">Became</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {list.map(([k, was, became]) => (
                                    <tr key={String(k)} className="border-t border-border">
                                      <td className="px-3 py-1.5 font-mono">{String(k)}</td>
                                      <td className="px-3 py-1.5 text-rose-600 dark:text-rose-400">{show(was)}</td>
                                      <td className="px-3 py-1.5 text-emerald-700 dark:text-emerald-400">{show(became)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                          {r.ip_address && (
                            <div className="text-[11px] text-muted-foreground mt-2">from {r.ip_address}</div>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}>Newer</Button>
          <span className="text-xs text-muted-foreground">
            {total === 0 ? '—' : `${offset + 1}–${Math.min(offset + LIMIT, total)} of ${total.toLocaleString('en-US')}`}
          </span>
          <Button variant="outline" size="sm" disabled={offset + LIMIT >= total}
            onClick={() => setOffset(offset + LIMIT)}>Older</Button>
        </div>

        <p className="text-xs text-muted-foreground">
          Entries showing “system” were made by an import or a background worker, with nobody signed in.
        </p>
      </main>
    </div>
  );
}
